import type { CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import {
  getCollectionEntity,
  mapDifficultyLevel,
} from 'bundles/unified-description-page-common/utils/dataTransformation';
import getBadgesFromS12nPageData from 'bundles/unified-description-page-common/utils/getBadgesFromS12nPageData';

export const useConvertSpecializationPageToCollectionEntity = () => {
  const courseraPlusOwnership = useCourseraPlusOwnership();

  return (specialization: ConsumerSpecializationPageFieldsFragment, entityIndex: number): CollectionEntity | null => {
    const {
      id,
      slug,
      name,
      cardImageUrl,
      difficultyLevel,
      partners,
      productVariant,
      ratings,
      skillTags,
      coBrandingEnabled,
      degrees,
      launchedAt,
      itemCounts,
      instructors,
    } = specialization;

    if (productVariant === 'STANDARD_SPECIALIZATION' || productVariant === 'PROFESSIONAL_CERTIFICATE') {
      let productType;
      if (productVariant === 'STANDARD_SPECIALIZATION') productType = 'SPECIALIZATION' as const;
      else if (productVariant === 'PROFESSIONAL_CERTIFICATE') productType = 'PROFESSIONAL CERTIFICATE' as const;
      else productType = 'SPECIALIZATION' as const;

      return getCollectionEntity({
        productId: id,
        name,
        slug,
        imageUrl: cardImageUrl ?? '',
        badges: getBadgesFromS12nPageData(specialization),
        productType,
        urlPath: productType === 'SPECIALIZATION' ? `/specializations/${slug}` : `/professional-certificates/${slug}`,
        reviewCount: ratings?.ratingCount ?? 0,
        rating: ratings?.averageFiveStarRating ?? 0,
        isPartOfCourseraPlus: courseraPlusOwnership?.isCourseraPlusEligibleProduct ?? false,
        isPathwayContent: !!degrees && degrees.length > 0,
        productDifficultyLevel: mapDifficultyLevel(difficultyLevel),
        partners: (partners ?? []).map((partner) => ({
          id: partner.id,
          name: partner.name,
          squareLogo: partner.squareLogo ?? '',
        })),
        skillsTags:
          skillTags?.map((skill) => ({
            id: skill.id,
            name: skill.name,
            isToolSoftware: skill.isToolSoftware,
          })) ?? [],
        coBrandingEnabled: coBrandingEnabled ?? undefined,
        lastUpdated: launchedAt ?? undefined,
        totalAssignmentCount: itemCounts?.totalAssignmentCount ?? undefined,
        hasTopInstructor: instructors?.some((instructor) => instructor.isTopInstructor) ?? false,
        entityIndex,
      });
    }

    return null;
  };
};
